'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Save, X } from 'lucide-react'
import type { SceneResult } from '@creanizer/types'

interface SceneEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (updatedResult: Partial<SceneResult>) => void
  sceneResult: SceneResult
  title?: string
}

export function SceneEditModal({
  isOpen,
  onClose,
  onSave,
  sceneResult,
  title = '編集'
}: SceneEditModalProps) {
  const [editedTitle, setEditedTitle] = useState(sceneResult.title)
  const [editedFullText, setEditedFullText] = useState(sceneResult.fullText)
  const [editedSummary, setEditedSummary] = useState(sceneResult.summary || '')

  // sceneResultが変更されたら編集中の値をリセット
  useEffect(() => {
    setEditedTitle(sceneResult.title)
    setEditedFullText(sceneResult.fullText)
    setEditedSummary(sceneResult.summary || '')
  }, [sceneResult])

  const handleSave = () => {
    // 文字数を再計算
    const wordCount = editedFullText.length
    const readingTime = `約${Math.ceil(wordCount / 400)}分`

    // 段落の再解析（簡易版）
    const paragraphs = editedFullText.split('\n\n').map((text, index) => ({
      index,
      text: text.trim(),
      type: (text.includes('「') ? 'dialogue' : 'description') as 'dialogue' | 'description',
      speaker: undefined
    }))

    onSave({
      title: editedTitle,
      fullText: editedFullText,
      summary: editedSummary || undefined,
      wordCount,
      readingTime,
      paragraphs
    })
    onClose()
  }

  const handleCancel = () => {
    // 編集内容をリセット
    setEditedTitle(sceneResult.title)
    setEditedFullText(sceneResult.fullText)
    setEditedSummary(sceneResult.summary || '')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="title">タイトル</Label>
            <Input
              id="title"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              placeholder="シーンのタイトル"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="summary">要約（オプション）</Label>
            <Textarea
              id="summary"
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              placeholder="シーンの要約"
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <Label htmlFor="fullText">本文</Label>
              <span className="text-sm text-muted-foreground">
                {editedFullText.length}文字
              </span>
            </div>
            <Textarea
              id="fullText"
              value={editedFullText}
              onChange={(e) => setEditedFullText(e.target.value)}
              placeholder="シーンの本文"
              rows={20}
              className="mt-1 font-mono text-sm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" />
            キャンセル
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}